import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { MealHabit, MealMenu, MealTexture } from '@/types/Meal.ts'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { UPDATE_DAYCARE_CHILD_MEAL } from '@/modules/API/meal/updateDaycareChildMeal.ts'
import {
    GET_DAYCARE_CHILD_MEAL,
    GET_DAYCARE_MEAL_HABITS,
    GET_DAYCARE_MEAL_MENUS,
    GET_DAYCARE_MEAL_TEXTURES
} from '@/modules/API/meal/getMealDaycareSettings.ts'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'

interface ChildMealSettings {
    mealMenus: MealMenu[]
    mealTextures: MealTexture[]
    mealHabits: MealHabit[]
}

export function useMealChildSettings(childId: number) {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { currentDaycare } = useCurrentDaycareInfos()

    const daycareId: number = currentDaycare.id

    const {
        data: dataMealChild,
        loading: loadingMealChild,
        refetch: refetchMealChild
    } = useQuery<{
        getDaycareChildMeal: ChildMealSettings
    }>(GET_DAYCARE_CHILD_MEAL, { variables: { daycareId, childId } })
    const {
        data: dataMealMenus,
        loading: loadingMealMenus,
        refetch: refetchMealMenus
    } = useQuery<{ getDaycareMealMenus: MealMenu[] }>(GET_DAYCARE_MEAL_MENUS, { variables: { daycareId } })
    const {
        data: dataMealTextures,
        loading: loadingMealTextures,
        refetch: refetchMealTextures
    } = useQuery<{
        getDaycareMealTextures: MealTexture[]
    }>(GET_DAYCARE_MEAL_TEXTURES, { variables: { daycareId } })
    const {
        data: dataMealHabits,
        loading: loadingMealHabits,
        refetch: refetchMealHabits
    } = useQuery<{ getDaycareMealHabits: MealHabit[] }>(GET_DAYCARE_MEAL_HABITS, { variables: { daycareId } })

    const { childMealMenus, childMealTextures, childMealHabits } = useMemo(() => {
        const meal: ChildMealSettings | undefined = dataMealChild?.getDaycareChildMeal
        function compareIds(a: { id: number }, b: { id: number }): number {
            return a.id.toString().localeCompare(b.id.toString())
        }

        return {
            childMealMenus: [...(meal?.mealMenus ?? [])].sort(compareIds),
            childMealTextures: [...(meal?.mealTextures ?? [])].sort(compareIds),
            childMealHabits: [...(meal?.mealHabits ?? [])].sort(compareIds)
        }
    }, [dataMealChild])

    const daycareMealMenus: MealMenu[] = dataMealMenus?.getDaycareMealMenus ?? []
    const daycareMealTextures: MealTexture[] = dataMealTextures?.getDaycareMealTextures ?? []
    const daycareMealHabits: MealHabit[] = dataMealHabits?.getDaycareMealHabits ?? []

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [selectedMealMenu, setSelectedMealMenu] = useState<string>(
        childMealMenus.length > 0 ? childMealMenus[0].id.toString() : ''
    )

    const optionsMealMenu: SelectOptionsDeprecated = [
        {
            options: [
                { name: t('noMealMenus'), value: '' },
                ...daycareMealMenus.map(
                    (mealMenu: MealMenu): SelectOptionDeprecated => ({
                        name: mealMenu.menu,
                        value: mealMenu.id.toString()
                    })
                )
            ]
        }
    ]

    const [selectedMealTexture, setSelectedMealTexture] = useState<string[]>(
        childMealTextures.map((mealTexture: MealTexture): string => mealTexture.id.toString())
    )

    const optionsMealTexture: SelectOptionsDeprecated = [
        {
            options: daycareMealTextures.map(
                (mealTexture: MealTexture): SelectOptionDeprecated => ({
                    name: mealTexture.texture,
                    value: mealTexture.id.toString()
                })
            )
        }
    ]

    const [selectedMealHabit, setSelectedMealHabit] = useState<string[]>(
        childMealHabits.map((mealHabit: MealHabit): string => mealHabit.id.toString())
    )

    const optionsMealHabit: SelectOptionsDeprecated = [
        {
            options: daycareMealHabits.map(
                (mealHabit: MealHabit): SelectOptionDeprecated => ({
                    name: mealHabit.habit,
                    value: mealHabit.id.toString()
                })
            )
        }
    ]

    const currentMenuId: string = childMealMenus.length > 0 ? childMealMenus[0].id.toString() : ''
    const hasChanges: boolean =
        selectedMealMenu !== currentMenuId ||
        [...selectedMealTexture].sort((id: string, id2: string): number => id.localeCompare(id2)).join(',') !==
            childMealTextures.map((m: MealTexture): string => m.id.toString()).join(',') ||
        [...selectedMealHabit].sort((id: string, id2: string): number => id.localeCompare(id2)).join(',') !==
            childMealHabits.map((m: MealHabit): string => m.id.toString()).join(',')

    const [updateDaycareChildMeal] = useMutation(UPDATE_DAYCARE_CHILD_MEAL, {
        variables: {
            daycareId,
            childId,
            menuIds: selectedMealMenu ? [parseInt(selectedMealMenu)] : [],
            textureIds: selectedMealTexture.map((mealTexture: string): number => parseInt(mealTexture)),
            habitIds: selectedMealHabit.map((mealHabit: string): number => parseInt(mealHabit))
        },
        onCompleted: () => {
            showNotificationSuccess(t('daycareChildMealUpdated'))
            refetchMealChild()
        },
        onError: () => {
            showNotificationError(t('daycareChildMealError'))
        }
    })

    useEffect(() => {
        setSelectedMealMenu(childMealMenus.length > 0 ? childMealMenus[0].id.toString() : '')
        setSelectedMealTexture(childMealTextures.map((mealTexture: MealTexture): string => mealTexture.id.toString()))
        setSelectedMealHabit(childMealHabits.map((mealHabit: MealHabit): string => mealHabit.id.toString()))
    }, [childMealTextures, childMealHabits, childMealMenus])

    const canEditMeal: boolean = !!user?.currentDaycareRights.canWriteMeal

    return {
        loading: loadingMealChild || loadingMealMenus || loadingMealTextures || loadingMealHabits,
        selectedMealMenu,
        setSelectedMealMenu,
        selectedMealTexture,
        setSelectedMealTexture,
        selectedMealHabit,
        setSelectedMealHabit,
        optionsMealMenu,
        optionsMealTexture,
        optionsMealHabit,
        canEditMeal,
        hasChanges,
        updateDaycareChildMeal,
        refetchMealMenus,
        refetchMealTextures,
        refetchMealHabits
    }
}
